// src/components/common/ActionButton.js
import React, { useRef, useLayoutEffect, useState } from 'react';

const ActionButton = ({
  onClick,
  children,
  icon: Icon,
  className,
  disabled,
  loading,
  type = 'button',
  variant = 'default', // 'default' or 'glassy'
  color = 'primary', // 'primary', 'green', 'orange', 'neutral', 'red', 'purple'
}) => {
  const buttonRef = useRef(null);
  const [minWidth, setMinWidth] = useState(0);

  useLayoutEffect(() => {
    if (buttonRef.current && !loading) {
      const width = buttonRef.current.offsetWidth;
      setMinWidth(width);
    }
  }, [loading, children]);

  const isGlassy = variant === 'glassy';

  // Map color prop to Tailwind classes using CSS variables
  const getColorClasses = (colorName) => {
    switch (colorName) {
      case 'primary':
        return 'bg-gradient-to-br from-[var(--color-glassy-primary)] to-[var(--color-glassy-primary-to)] shadow-md shadow-orange-500/40';
      case 'orange':
        return 'bg-gradient-to-br from-[var(--color-glassy-orange)] to-[var(--color-glassy-orange-to)] shadow-md shadow-orange-500/40';
      case 'green':
        return 'bg-gradient-to-br from-[var(--color-glassy-green)] to-[var(--color-glassy-green-to)] shadow-md shadow-green-500/40';
      case 'blue':
        return 'bg-gradient-to-br from-[var(--color-glassy-blue)] to-[var(--color-glassy-blue-to)] shadow-md shadow-blue-500/40';
      case 'neutral':
        return 'bg-gradient-to-br from-[var(--color-glassy-neutral)] to-[var(--color-glassy-neutral-to)] shadow-md shadow-gray-500/40';
      case 'red':
        return 'bg-gradient-to-br from-[var(--color-glassy-red)] to-[var(--color-glassy-red-to)] shadow-md shadow-red-500/40';
      case 'purple':
        return 'bg-gradient-to-br from-[var(--color-glassy-purple)] to-[var(--color-glassy-purple-to)] shadow-md shadow-purple-500/40';
      case 'yellow':
        return 'bg-gradient-to-br from-[var(--color-glassy-yellow)] to-[var(--color-glassy-yellow-to)] shadow-md shadow-yellow-500/40';
      case 'emerald':
        return 'bg-gradient-to-br from-[var(--color-glassy-emerald)] to-[var(--color-glassy-emerald-to)] shadow-md shadow-emerald-500/40';
      case 'amber':
        return 'bg-gradient-to-br from-[var(--color-glassy-amber)] to-[var(--color-glassy-amber-to)] shadow-md shadow-amber-500/40';
      case 'violet':
        return 'bg-gradient-to-br from-[var(--color-glassy-violet)] to-[var(--color-glassy-violet-to)] shadow-md shadow-violet-500/40';
      case 'pink':
        return 'bg-gradient-to-br from-[var(--color-glassy-pink)] to-[var(--color-glassy-pink-to)] shadow-md shadow-pink-500/40';
      default:
        return 'bg-gradient-to-br from-[var(--color-glassy-primary)] to-[var(--color-glassy-primary-to)] shadow-md shadow-orange-500/40';
    }
  };

  const buttonStyle = {
    minWidth: minWidth > 0 ? `${minWidth}px` : 'auto',
  };

  const buttonClasses = isGlassy
    ? `
        relative inline-flex items-center justify-center font-semibold rounded-xl
        backdrop-blur-sm border border-white/20 overflow-hidden
        transition-all duration-200 ease-out
        disabled:opacity-60 disabled:cursor-not-allowed
        text-sm sm:text-md
        ${getColorClasses(color)}
        ${!disabled ? 'hover:shadow-xl hover:-translate-y-0.5 active:scale-[.98]' : ''}
        ${className}
      `
    : `
        inline-flex items-center justify-center font-medium rounded-md shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed text-sm sm:text-md ${className}
      `;

  return (
    <button ref={buttonRef} type={type} onClick={onClick} disabled={disabled || loading} style={buttonStyle} className={buttonClasses}>
      {isGlassy && <div className="absolute inset-0 bg-gradient-to-br from-white/30 to-transparent opacity-70 dark:from-white/10 dark:opacity-50" />}

      <div className={`${isGlassy ? 'relative z-10 flex items-center text-white drop-shadow-sm' : 'flex items-center'}`}>
        {loading ? (
          <>
            <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </>
        ) : (
          <>
            {Icon && <Icon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />}
            {children}
          </>
        )}
      </div>
    </button>
  );
};

export default ActionButton;
